import React, { useEffect, useMemo, useState } from "react";

import {
  faDiagramNested,
  faExclamationCircle,
  faPlus,
  faRobot,
  faRotateRight,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { AgentVersionNameExpandable } from "src/agents/AgentVersionName";
import { CreateAgentVersionModal } from "src/agents/CreateAgentVersionModal";
import { EditAgentVersionModal } from "src/agents/EditAgentVersionModal";
import { DeleteAgentVersionModal } from "src/agents/modals/DeleteAgentVersionModal";
import { AgentVersion } from "src/agents/types";
import { useAgentVersionMenuOptions } from "src/agents/useAgentVersionMenuOptions";
import { useIsPrebuiltReadOnly } from "src/agents/usePrebuiltReadOnly";
import {
  READ_FILE_TOOL_NAME,
  useAgent,
  useAgentVersions,
  useDeleteAgentVersion,
  usePrefetchAgentModels,
  usePublishAgentVersion,
} from "src/api/agents/management";
import { DecisionEnvironment } from "src/api/types";
import { TOOL_ICON_CONFIG } from "src/base-components/agents/ToolCard";
import { Button } from "src/base-components/Button";
import { DisplayDateTime } from "src/base-components/Date";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import type { ToolType } from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import { UserFromId } from "src/flow/User";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { tableWidth } from "src/layout/constants";
import { AgentHistoryKeys } from "src/router/SearchParams";
import {
  getUrlToAgentHistoryPage,
  getUrlToAgentVersionPage,
} from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentVersionsPage: React.FC = () => {
  const { agentId } = useParamsDecode<AgentVersionsPageParams>();
  const { data: agent } = useAgent(agentId);
  const isReadOnly = useIsPrebuiltReadOnly(agent);
  const [filter, setFilter] = useState<FilterValue | null>(null);
  const {
    isOpen: isCreateModalOpen,
    data: createModalData,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
  } = useModal<{ versionId?: string; startFromScratch: boolean }>();

  useDocumentTitle(["Agent Versions"]);

  // Prefetch available models for agent configuration
  const prefetchModels = usePrefetchAgentModels();
  useEffect(() => {
    prefetchModels();
  }, [prefetchModels]);

  const visibleTables = filter === null ? "both" : filter;

  return (
    <div className="flex h-full flex-col" data-loc="agent-versions-page">
      <SubHeader title="Versions">
        <SubHeaderFilter
          dataLoc="filter-versions"
          options={filterOptions}
          selected={filter}
          onChange={setFilter}
        />
        {!isReadOnly && (
          <SubHeader.Button
            dataLoc="create-version-button"
            icon={faPlus}
            tooltip="Create version"
            onClick={() => openCreateModal({ startFromScratch: true })}
          />
        )}
      </SubHeader>
      <div className="decideScrollbar flex h-full min-w-[300px] flex-col overflow-x-auto">
        <AgentVersionsContent
          agentId={agentId}
          isReadOnly={isReadOnly}
          visibleTables={visibleTables}
          onOpenCreateModal={openCreateModal}
        />
      </div>
      <CreateAgentVersionModal
        agentId={agentId}
        open={isCreateModalOpen}
        startFromScratch={createModalData?.startFromScratch ?? false}
        versionId={createModalData?.versionId}
        onClose={closeCreateModal}
      />
    </div>
  );
};

type AgentVersionsPageParams = {
  orgId: string;
  wsId: string;
  agentId: string;
};

const filterOptions = [
  { key: "published" as const, value: "Published" },
  { key: "draft" as const, value: "Draft" },
];

type FilterValue = (typeof filterOptions)[number]["key"];

type AgentVersionsContentProps = {
  agentId: string;
  isReadOnly: boolean;
  visibleTables: "published" | "draft" | "both";
  onOpenCreateModal: (data?: {
    versionId?: string;
    startFromScratch: boolean;
  }) => void;
};

const AgentVersionsContent: React.FC<AgentVersionsContentProps> = ({
  agentId,
  isReadOnly,
  visibleTables,
  onOpenCreateModal,
}) => {
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();
  const {
    data: versions,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useAgentVersions(agentId);

  const publishMutation = usePublishAgentVersion();
  const deleteMutation = useDeleteAgentVersion();

  const {
    isOpen: isDeleteModalOpen,
    data: versionToDelete,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal<AgentVersion>();

  const {
    isOpen: isEditModalOpen,
    data: versionToEdit,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<AgentVersion>();

  const handleDuplicate = (version: AgentVersion) => {
    onOpenCreateModal({ versionId: version.id, startFromScratch: false });
  };

  const handleEdit = (version: AgentVersion) => {
    openEditModal(version);
  };

  const handleViewHistory = (version: AgentVersion) => {
    navigate(
      getUrlToAgentHistoryPage(orgId, wsId, agentId, {
        [AgentHistoryKeys.VersionId]: version.id,
        [AgentHistoryKeys.Environment]:
          version.status === "draft"
            ? DecisionEnvironment.SANDBOX
            : DecisionEnvironment.LIVE,
      }),
    );
  };

  const handlePublish = (version: AgentVersion) => {
    if (version.status === "published") {
      logger.error("Cannot publish published version", version);
      return;
    }

    publishMutation.mutate({
      agentId,
      versionId: version.id,
      etag: version.etag,
    });
  };

  const handleDelete = (version: AgentVersion) => {
    openDeleteModal(version);
  };

  const handleConfirmDelete = () => {
    if (!versionToDelete) return;

    deleteMutation.mutate({
      agentId,
      versionId: versionToDelete.id,
    });
    closeDeleteModal();
  };

  const publishedVersions = (
    versions?.filter((version) => version.status === "published") ?? []
  ).sort((a, b) => {
    const dateA = a.metadata.published_at ?? "";
    const dateB = b.metadata.published_at ?? "";
    return dateB.localeCompare(dateA);
  });

  const draftVersions = (
    versions?.filter((version) => version.status === "draft") ?? []
  ).sort((a, b) => {
    const dateA = a.metadata.updated_at ?? "";
    const dateB = b.metadata.updated_at ?? "";
    return dateB.localeCompare(dateA);
  });

  if (isError) {
    return (
      <div className="p-5">
        <WhitePane>
          <EmptyState
            actionButton={{
              icon: faRotateRight,
              label: "Retry",
              onClick: refetch,
              isDisabled: isFetching,
            }}
            dataLoc="agent-versions-error-state"
            description="We're having trouble loading Agent versions. Please try again later"
            headline="Error loading versions"
            icon={faExclamationCircle}
            variant="error"
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <>
      <div
        className={twJoin(
          "mx-auto flex w-full flex-col gap-y-5 p-7",
          tableWidth,
        )}
      >
        {!isLoading &&
          publishedVersions.length === 0 &&
          draftVersions.length === 0 && (
            <WhitePane>
              <EmptyState
                actionButton={
                  isReadOnly
                    ? undefined
                    : {
                        label: "Create version",
                        icon: faPlus,
                        onClick: () =>
                          onOpenCreateModal({ startFromScratch: true }),
                      }
                }
                dataLoc="agent-versions-empty-state"
                description={
                  isReadOnly
                    ? "There are no versions for this Agent yet."
                    : "Create your first version to get started"
                }
                headline="No versions yet"
                icon={faRobot}
              />
            </WhitePane>
          )}

        {visibleTables !== "draft" &&
          (publishedVersions.length > 0 || isLoading) && (
            <AgentVersionsTable
              canDelete={false}
              isLoading={isLoading}
              isReadOnly={isReadOnly}
              versions={publishedVersions}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onPublish={handlePublish}
              onViewHistory={handleViewHistory}
            />
          )}

        {visibleTables !== "published" && draftVersions.length > 0 && (
          <AgentVersionsTable
            canDelete={true}
            isLoading={isLoading}
            isReadOnly={isReadOnly}
            versions={draftVersions}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
            onPublish={handlePublish}
            onViewHistory={handleViewHistory}
          />
        )}
      </div>

      <DeleteAgentVersionModal
        isOpen={isDeleteModalOpen}
        version={versionToDelete}
        workspaceId={wsId}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
      <EditAgentVersionModal
        open={isEditModalOpen}
        version={versionToEdit ?? null}
        onClose={closeEditModal}
      />
    </>
  );
};

const AgentVersionsTable: React.FC<{
  versions: AgentVersion[];
  isLoading: boolean;
  canDelete: boolean;
  isReadOnly: boolean;
  onDuplicate: (version: AgentVersion) => void;
  onEdit?: (version: AgentVersion) => void;
  onPublish: (version: AgentVersion) => void;
  onDelete: (version: AgentVersion) => void;
  onViewHistory: (version: AgentVersion) => void;
}> = ({
  versions,
  isLoading,
  canDelete,
  isReadOnly,
  onDuplicate,
  onEdit,
  onPublish,
  onDelete,
  onViewHistory,
}) => {
  const navigate = useNavigate();
  const { agentId, orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();

  const actions: Parameters<typeof Table>[0]["actions"] = {
    onDuplicate,
    onPublish,
    onViewHistory,
  };
  if (canDelete) {
    actions.onDelete = onDelete;
  }
  if (onEdit) {
    actions.onEdit = onEdit;
  }

  const columns = useMemo(() => buildColumns(isReadOnly), [isReadOnly]);

  return (
    <WhitePane>
      <Table
        actions={actions}
        columns={columns}
        data={versions}
        dataLoc="agent-versions-table"
        frameClassName="w-full"
        isLoading={isLoading}
        loadingRowsCount={3}
        rowClassName="cursor-pointer hover:bg-gray-50 align-top"
        rowPropsGetter={(row) => ({
          "data-loc": `agent-version-row-${row.original.name}`,
          onClick: () => {
            navigate(
              getUrlToAgentVersionPage(orgId, wsId, agentId, row.original.id),
            );
          },
        })}
      />
    </WhitePane>
  );
};

const helper = createColumnHelper<AgentVersion>();

const buildColumns = (isReadOnly: boolean) => [
  helper.accessor("name", {
    header: (info) => {
      const isDraftTable =
        info.table.getRowModel().rows.at(0)?.original.status === "draft";
      return (
        <Header info={info}>
          <div className="pl-5 font-inter-semibold-13px text-gray-800">
            {isDraftTable ? "Draft" : "Published"}
          </div>
        </Header>
      );
    },
    cell: (info) => (
      <Cell className="h-auto w-80 items-start py-3 pl-5" info={info}>
        <AgentVersionNameExpandable
          description={info.row.original.description}
          name={info.getValue()}
          status={info.row.original.status}
        />
      </Cell>
    ),
    size: 380,
  }),
  helper.display({
    id: "author",
    header: (info) => {
      const isDraftTable =
        info.table.getRowModel().rows.at(0)?.original.status === "draft";
      return (
        <Header info={info}>
          {isDraftTable ? "Created by" : "Published by"}
        </Header>
      );
    },
    cell: (info) => {
      const version = info.row.original;
      const userId =
        version.status === "published"
          ? version.metadata.published_by_id
          : version.metadata.created_by_id;

      return (
        <Cell info={info}>{userId ? <UserFromId userId={userId} /> : "-"}</Cell>
      );
    },
    size: 320,
  }),
  helper.display({
    id: "date",
    header: (info) => {
      const isDraftTable =
        info.table.getRowModel().rows.at(0)?.original.status === "draft";
      return (
        <Header info={info}>
          {isDraftTable ? "Last edited on" : "Published on"}
        </Header>
      );
    },
    cell: (info) => {
      const version = info.row.original;
      const date =
        version.status === "published"
          ? version.metadata.published_at
          : version.metadata.updated_at;

      return (
        <Cell info={info}>{date ? <DisplayDateTime value={date} /> : "-"}</Cell>
      );
    },
    size: 300,
  }),
  helper.display({
    id: "tools_used",
    header: (info) => <Header info={info}>Tools used</Header>,
    cell: (info) => {
      const tools = info.row.original.configuration.tools ?? [];

      return (
        <Cell info={info}>
          <ToolsCell tools={tools} />
        </Cell>
      );
    },
    size: 400,
  }),
  helper.display({
    id: "actions",
    cell: ({ row, table }) => {
      if (!table.options.meta?.actions) return null;

      return (
        <AgentVersionActionsMenu
          isReadOnly={isReadOnly}
          version={row.original}
          onDelete={table.options.meta?.actions.onDelete}
          onDuplicate={table.options.meta?.actions.onDuplicate}
          onEdit={table.options.meta?.actions.onEdit}
          onPublish={table.options.meta?.actions.onPublish}
          onViewHistory={table.options.meta?.actions.onViewHistory}
        />
      );
    },
    size: 100,
  }),
];

const ToolsCell: React.FC<{
  tools: { name: string; type?: string }[];
}> = ({ tools }) => {
  const [isOpen, setIsOpen] = useState(false);
  const firstTool = tools.at(0);
  const remainingCount = tools.length - 1;

  if (!firstTool) {
    return (
      <span className="font-inter-normal-12px text-gray-500">No tools</span>
    );
  }

  const getToolIcon = (type?: string) =>
    TOOL_ICON_CONFIG[type as ToolType]?.icon ?? faDiagramNested;

  const getToolName = (tool: { name: string; type?: string }) =>
    tool.type === "read_file" ? READ_FILE_TOOL_NAME : tool.name;

  return (
    <CustomPopover
      button={
        <div className="flex cursor-default items-center gap-2">
          <Pill variant="outlined-white">
            <Pill.Icon icon={getToolIcon(firstTool.type)} />
            <Pill.Text>{getToolName(firstTool)}</Pill.Text>
          </Pill>
          {remainingCount > 0 && (
            <span className="font-inter-normal-12px text-gray-500">
              +{remainingCount} more
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      placement="bottom-start"
      wrapperClassName="h-auto"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="w-70 cursor-default p-4">
        <div className="mb-5 font-inter-medium-13px text-gray-800">
          Tools agent uses
        </div>
        <div className="flex flex-col gap-4">
          {tools.map((tool) => (
            <div key={getToolName(tool)} className="flex items-center gap-2">
              <Icon icon={getToolIcon(tool.type)} size="xs" />
              <span className="font-inter-normal-13px text-gray-800">
                {getToolName(tool)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </CustomPopover>
  );
};

const AgentVersionActionsMenu: React.FC<{
  version: AgentVersion;
  isReadOnly: boolean;
  onDuplicate: (version: AgentVersion) => void;
  onEdit?: (version: AgentVersion) => void;
  onPublish: (version: AgentVersion) => void;
  onDelete?: (version: AgentVersion) => void;
  onViewHistory: (version: AgentVersion) => void;
}> = ({
  version,
  isReadOnly,
  onDuplicate,
  onEdit,
  onPublish,
  onDelete,
  onViewHistory,
}) => {
  const options = useAgentVersionMenuOptions({
    isDraft: version.status === "draft",
    configuration: version.configuration,
    onEdit: onEdit ? () => onEdit(version) : undefined,
    onDuplicate: () => onDuplicate(version),
    onPublish: () => onPublish(version),
    onDelete: onDelete ? () => onDelete(version) : undefined,
    isReadOnly,
  });

  return (
    <div className="flex h-12 items-center justify-end gap-2 pr-5">
      <div className="opacity-0 focus-within:opacity-100 group-hover:opacity-100">
        <Button
          dataLoc={`agent-version-run-history-${version.id}`}
          iconLeft={faWavePulse}
          size="sm"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            onViewHistory(version);
          }}
        >
          <span className="text-nowrap">Run history</span>
        </Button>
      </div>
      {options.length > 0 && (
        <EllipsisOptionsDropdown
          buttonClassName="text-gray-300 w-5 opacity-0 group-hover:opacity-100 data-[menu-open=true]:opacity-100"
          dataLoc="agent-version-actions"
          elements={options}
          ellipsisDirection="vertical"
          timeoutDuration={200}
        />
      )}
    </div>
  );
};
